import { useEffect, useRef, useState, useId } from 'react'

// Site key Turnstile de Limpack — es PÚBLICO, va en el frontend
// Hostnames autorizados: limpack.uy, panel.limpack.uy, tienda.limpack.uy
const TURNSTILE_SITE_KEY = '0x4AAAAAADP-6T-hSaZPvkdp'

// Carga el script de Turnstile una sola vez (compartido entre instancias del widget)
let scriptPromise = null
function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.reject('SSR')
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => { scriptPromise = null; reject(new Error('No se pudo cargar Turnstile')) }
    document.head.appendChild(s)
  })
  return scriptPromise
}

/**
 * <TurnstileWidget onToken={tok => setToken(tok)} />
 *
 * Renderiza el challenge de Cloudflare Turnstile. Cuando el usuario lo resuelve
 * (la mayoría de las veces es invisible), llama onToken(token).
 * Si falla o expira, llama onToken(null) para que el caller deshabilite el submit.
 *
 * Props:
 *   - onToken: callback (token: string | null) => void
 *   - theme: 'auto' | 'light' | 'dark' (default 'auto')
 *   - size: 'normal' | 'compact' | 'invisible' (default 'normal')
 *   - action: opcional, etiqueta del action (ej. 'signup', 'checkout') para analytics
 */
export default function TurnstileWidget({ onToken, theme = 'auto', size = 'normal', action }) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const id = useId().replace(/:/g, '_')
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          size,
          action,
          callback: (token) => onToken?.(token),
          'error-callback': () => { setError('error'); onToken?.(null) },
          'expired-callback': () => onToken?.(null),
          'timeout-callback': () => { setError('timeout'); onToken?.(null) },
        })
      })
      .catch(e => {
        console.warn('Turnstile no disponible:', e?.message || e)
        setError('script')
        // Si Turnstile no carga (CSP, red, etc.), permitir el submit pero sin token.
        // El backend todavía valida y va a rechazar — pero al menos el botón no queda muerto.
        onToken?.(null)
      })

    return () => {
      cancelled = true
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="ts-wrap">
      <div ref={containerRef} id={`ts-${id}`} />
      {error && (
        <div className="ts-err">
          No se pudo cargar la verificación anti-bot. Recargá la página o probá desde otra red.
        </div>
      )}
      <style>{`
        .ts-wrap { display: flex; flex-direction: column; align-items: center; gap: 6px; margin: 8px 0 }
        .ts-err { font-size: 12px; color: #b91c1c; text-align: center; max-width: 320px }
      `}</style>
    </div>
  )
}
